import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Segmented, Spin, message } from "antd";
import {
  DownloadOutlined,
  PauseCircleOutlined,
  PhoneOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import {
  fetchResponseRecording,
  listResponseConversations,
} from "pages/VoiceCaptureManagement/feedbackService";
import { callFileName, conversationAsText, formatClock } from "./responseFormat";
import styles from "./style.module.scss";

function saveFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * A phone response's call: each question with the agent's and the customer's own words, the whole
 * conversation, the recording (fetched only when played or downloaded) and both as downloads.
 * The page's CSP keeps blob: URLs out of <audio>, so the recording plays through Web Audio,
 * like the Sonic test panel.
 */
const CallConversation = ({ instrumentId, response, number, surveyTitle }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({ loading: true, conversation: null, failed: false });
  const [tab, setTab] = useState("pairs");
  const [audioLoading, setAudioLoading] = useState(false);
  const [playingKey, setPlayingKey] = useState(null);
  const recordingRef = useRef({ responseId: null, blob: null, buffer: null, ctx: null, node: null });

  const stopNode = () => {
    const rec = recordingRef.current;
    if (rec.node) {
      try {
        rec.node.stop();
      } catch (_) {
        // already ended
      }
      rec.node = null;
    }
  };

  const stopPlayback = () => {
    stopNode();
    setPlayingKey(null);
  };

  useEffect(() => {
    let active = true;
    setState({ loading: true, conversation: null, failed: false });
    setTab("pairs");
    setPlayingKey(null);
    listResponseConversations(instrumentId, response.id)
      .then((found) => active && setState({ loading: false, conversation: found[String(response.id)] || null, failed: false }))
      .catch(() => active && setState({ loading: false, conversation: null, failed: true }));
    return () => {
      active = false;
      stopNode();
    };
  }, [instrumentId, response.id]);

  useEffect(
    () => () => {
      const rec = recordingRef.current;
      if (rec.ctx) rec.ctx.close().catch(() => {});
    },
    []
  );

  const { loading, conversation, failed } = state;
  const hasRecording = !!conversation?.has_recording;

  const loadBlob = async () => {
    const rec = recordingRef.current;
    if (rec.blob && rec.responseId === response.id) return rec.blob;
    setAudioLoading(true);
    const blob = await fetchResponseRecording(instrumentId, response.id).catch(() => null);
    setAudioLoading(false);
    if (!blob) {
      message.error(t("feedbackCapture.responsesPage.call_recording_error"));
      return null;
    }
    Object.assign(rec, { responseId: response.id, blob, buffer: null });
    return blob;
  };

  const loadBuffer = async () => {
    const rec = recordingRef.current;
    if (rec.buffer && rec.responseId === response.id) return rec.buffer;
    const blob = await loadBlob();
    if (!blob) return null;
    if (!rec.ctx) rec.ctx = new (window.AudioContext || window.webkitAudioContext)();
    try {
      rec.buffer = await rec.ctx.decodeAudioData(await blob.arrayBuffer());
      return rec.buffer;
    } catch (_) {
      message.error(t("feedbackCapture.responsesPage.call_recording_error"));
      return null;
    }
  };

  // The whole call ("all"), or one line from its start to the next line's start; again stops it.
  const play = async (key, start = 0) => {
    if (playingKey === key) {
      stopPlayback();
      return;
    }
    stopPlayback();
    const buffer = await loadBuffer();
    if (!buffer) return;
    const rec = recordingRef.current;
    if (rec.ctx.state === "suspended") await rec.ctx.resume();
    const node = rec.ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(rec.ctx.destination);
    const offset = Math.max(0, Math.min(Number(start) || 0, buffer.duration));
    const next = key === "all" ? null : (conversation.turns || []).find((turn) => turn.start > offset);
    node.onended = () => {
      if (recordingRef.current.node === node) {
        recordingRef.current.node = null;
        setPlayingKey(null);
      }
    };
    if (next) node.start(0, offset, next.start - offset);
    else node.start(0, offset);
    rec.node = node;
    setPlayingKey(key);
  };

  const downloadTranscript = () => {
    const text = conversationAsText(response, conversation, { surveyTitle, number }, t);
    // The byte order mark lets Windows editors open the Turkish characters correctly.
    saveFile(
      new Blob(["﻿", text], { type: "text/plain;charset=utf-8" }),
      callFileName(surveyTitle, number, "transcript")
    );
  };

  const downloadAudio = async () => {
    const blob = await loadBlob();
    if (blob) saveFile(blob, callFileName(surveyTitle, number, "audio"));
  };

  const renderLine = (role, text, start, key) => {
    const clock = formatClock(start);
    const lineKey = `line-${start}`;
    return (
      <div key={key} className={`${styles.callLine} ${role === "agent" ? styles.callAgent : ""}`}>
        <span className={styles.callSpeaker}>
          {role === "agent"
            ? t("feedbackCapture.responsesPage.call_agent")
            : t("feedbackCapture.responsesPage.call_customer")}
        </span>
        {clock && hasRecording ? (
          <button
            type="button"
            className={`${styles.callClock} ${playingKey === lineKey ? styles.callClockPlaying : ""}`}
            onClick={() => play(lineKey, start)}
            aria-label={t("feedbackCapture.responsesPage.call_jump", { time: clock })}
          >
            {playingKey === lineKey ? <PauseCircleOutlined /> : null} {clock}
          </button>
        ) : clock ? (
          <span className={styles.callClockPlain}>{clock}</span>
        ) : null}
        <span className={styles.callText}>{text}</span>
      </div>
    );
  };

  let body;
  if (loading) {
    body = <Spin size="small" />;
  } else if (failed || !conversation) {
    body = (
      <div className={styles.muted}>
        {t(failed ? "feedbackCapture.responsesPage.call_load_error" : "feedbackCapture.responsesPage.call_unavailable")}
      </div>
    );
  } else {
    body = (
      <>
        <Segmented
          size="small"
          value={tab}
          onChange={setTab}
          options={[
            { value: "pairs", label: t("feedbackCapture.responsesPage.call_tab_pairs") },
            { value: "transcript", label: t("feedbackCapture.responsesPage.call_tab_transcript") },
          ]}
        />
        {tab === "pairs" ? (
          <div className={styles.callList}>
            {(conversation.pairs || []).map((pair, index) => (
              <div key={pair.question_id || index} className={styles.callPair}>
                <div className={styles.qaQuestion}>
                  {index + 1}. {pair.question_text || pair.question_id}
                </div>
                {pair.asked ? (
                  <>
                    {renderLine("agent", pair.asked, pair.asked_at)}
                    {renderLine(
                      "customer",
                      pair.answer || t("feedbackCapture.responsesPage.call_no_answer"),
                      pair.answer_at
                    )}
                  </>
                ) : (
                  <div className={styles.muted}>{t("feedbackCapture.responsesPage.call_not_asked")}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.callList}>
            {(conversation.turns || []).map((turn, index) => renderLine(turn.role, turn.text, turn.start, index))}
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.callBox}>
      <div className={styles.callHead}>
        <span className={styles.callTitle}>
          <PhoneOutlined /> {t("feedbackCapture.responsesPage.call_title")}
        </span>
        {conversation ? (
          <div className={styles.callActions}>
            {hasRecording ? (
              <Button
                size="small"
                icon={playingKey === "all" ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                loading={audioLoading && !playingKey}
                onClick={() => play("all")}
              >
                {t(
                  playingKey === "all"
                    ? "feedbackCapture.responsesPage.call_stop"
                    : "feedbackCapture.responsesPage.call_play"
                )}
              </Button>
            ) : null}
            <Button size="small" icon={<DownloadOutlined />} onClick={downloadTranscript}>
              {t("feedbackCapture.responsesPage.call_download_transcript")}
            </Button>
            {hasRecording ? (
              <Button size="small" icon={<DownloadOutlined />} onClick={downloadAudio}>
                {t("feedbackCapture.responsesPage.call_download_audio")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      {body}
    </div>
  );
};

export default CallConversation;
